<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "TOCTemp2",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        toc: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
    },
    data() {
        return {
            pages: []
        };
    },
    watch: {
        toc: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.splitContentIntoPages();
                });
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.splitContentIntoPages();
        });
    },
    methods: {
        splitContentIntoPages() {
            if (!this.toc || !this.toc.pages) return;

            const itemsPerPage = parseInt(this.toc.itemsPerPage);
            const half = Math.ceil(itemsPerPage / 2);
            const projects = this.toc.projects || [];
            let allTocItems = [...this.toc.pages];

            this.pages = [];
            let pagesProjectsIndex = 0;

            while (allTocItems.length > 0) {
                let topItems = allTocItems.splice(0, half);
                let bottomItems = allTocItems.splice(0, itemsPerPage - topItems.length);

                this.pages.push({
                    topItems: topItems,
                    bottomItems: bottomItems,
                    project: projects[pagesProjectsIndex] || null
                });
                pagesProjectsIndex++;

                if (this.pages.length > 50) break;
            }
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': toc.style.colorScheme && toc.style.colorScheme.header,
        '--body-color': toc.style.colorScheme && toc.style.colorScheme.body,
        '--footer-color': toc.style.colorScheme && toc.style.colorScheme.footer,
        '--heading-color': toc.style.heading.color,
        '--heading-size': toc.style.heading.size + 'px',
        '--heading-weight': toc.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': toc.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': toc.style.heading.underline ? 'underline' : 'none',
        '--heading-font': toc.style.heading.font,

        '--label-color': toc.style.label.color,
        '--label-size': toc.style.label.size + 'px',
        '--label-weight': toc.style.label.bold ? 'bold' : 'normal',
        '--label-style': toc.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': toc.style.label.underline ? 'underline' : 'none',
        '--label-font': toc.style.label.font,

        '--text-color': toc.style.text.color || '#000000',
        '--text-size': toc.style.text.size + 'px',
        '--text-weight': toc.style.text.bold ? 'bold' : 'normal',
        '--text-style': toc.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': toc.style.text.underline ? 'underline' : 'none',
        '--text-font': toc.style.text.font,
    }">
        <div class="sub-container" v-for="(page, pageIndex) in pages" :key="pageIndex">

            <!-- Top Section -->
            <div class="section">
                <div class="left-side">
                    <h1 v-if="pageIndex === 0" class="main-title">
                        <SpellCheckedText :value="toc.title || ''" @input="toc.title = $event" />
                    </h1>
                </div>
                <div class="separator top"></div>
                <div class="right-side">
                    <div v-for="(item, idx) in page.topItems" :key="idx" class="toc-item">
                        <span class="heading">
                            <SpellCheckedText :value="item.heading || ''" @input="item.heading = $event" />
                        </span>
                        <span class="page-num">{{ String(item.page || '').padStart(2, '0') }}</span>
                    </div>
                </div>
            </div>

            <!-- Middle Project Image -->
            <div class="middle-image">
                <PhotoBox v-if="page.project" class="photobox" :project="page.project"
                    :bgColor="toc.style.colorScheme && toc.style.colorScheme.body" />
                <div v-else class="imageBox"></div>
            </div>

            <!-- Bottom Section -->
            <div class="section bottom-section">
                <div class="left-side"></div>
                <div class="separator bottom"></div>
                <div class="right-side">
                    <div v-for="(item, idx) in page.bottomItems" :key="idx" class="toc-item">
                        <span class="heading">
                            <SpellCheckedText :value="item.heading" @input="item.heading = $event" />
                        </span>
                        <span class="page-num">{{ String(item.page).padStart(2, '0') }}</span>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 211mm;
    min-height: 296.83mm;
    position: relative;
    box-sizing: border-box;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .sub-container {
        display: flex;
        flex-direction: column;
        height: 296.83mm;
        overflow: hidden;
        padding: 20px 0;
        background: white;

        .section {
            flex: 1;
            display: flex;
            padding: 20px 30px;
            min-height: 0;

            .left-side {
                width: 35%;
                display: flex;
                align-items: center;
                justify-content: end;
                padding-right: 30px;

                .main-title {
                    color: var(--heading-color);
                    font-size: var(--heading-size);
                    font-weight: var(--heading-weight);
                    font-family: var(--heading-font);
                    font-style: var(--heading-style);
                    text-decoration: var(--heading-decoration);
                    text-align: right;
                    line-height: 1.1;
                    margin: 0;
                    text-transform: uppercase;
                }
            }

            .separator {
                width: 3px;
                height: 100%;

                &.top {
                    background: var(--header-color);
                }

                &.bottom {
                    background: var(--footer-color);
                }
            }

            .right-side {
                flex: 1;
                display: flex;
                flex-direction: column;
                padding-left: 30px;
                overflow: hidden;

                .toc-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: start;
                    margin-bottom: 15px;
                    gap: 15px;

                    .heading {
                        flex: 1;
                        color: var(--text-color);
                        font-size: var(--text-size);
                        font-weight: var(--text-weight);
                        font-family: var(--text-font);
                        font-style: var(--text-style);
                        text-decoration: var(--text-decoration);
                        min-width: 0;
                    }

                    .page-num {
                        min-width: 25px;
                        flex-shrink: 0;
                        text-align: right;
                        white-space: nowrap;
                        color: var(--label-color);
                        font-size: var(--label-size);
                        font-weight: var(--label-weight);
                        font-family: var(--label-font);
                        font-style: var(--label-style);
                        text-decoration: var(--label-decoration);
                    }
                }
            }
        }

        .middle-image {
            height: 350px;
            width: 100%;
            margin: 20px 0;

            .imageBox {
                width: 100%;
                height: 100%;
                background: var(--body-color);
            }
        }
    }
}
</style>
